import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaBars, FaArrowLeft, FaArrowUp } from "react-icons/fa";

import MedalSummary from "../../components/teamReport/medalSummary";
import TournamentParticipation from "../../components/teamReport/tournamentParticipation";
import PlayerPerformance from "../../components/teamReport/playerPerformance";
import AgeGroupPerformance from "../../components/teamReport/ageGroupPerformance";
import RankingProgression from "../../components/teamReport/rankingProgression";
import GenderSplit from "../../components/teamReport/genderSplit";

import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";

// Generic Section Component
function Section({ id, title, children, expandRef }) {
  const [open, setOpen] = useState(false);

  // Allow outside expand
  useEffect(() => {
    if (expandRef) expandRef.current = () => setOpen(true);
  }, [expandRef]);

  return (
    <section id={id} className="border rounded-lg bg-white shadow-sm">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center p-3 bg-gray-100 rounded-t-lg"
      >
        <p className="text-left font-bold">{title}</p>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open && <div className="p-4">{children}</div>}
    </section>
  );
}

export default function TeamReportPage() {
  const token = sessionStorage.getItem("access_token");
  const [players, setPlayers] = useState([]);
  const [results, setResults] = useState([]);
  const [ajss, setAjss] = useState([]);
  const [activities, setActivities] = useState([]);

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const navigate = useNavigate();
  const { showToast } = useToast();

  // Section Refs
  const expandRefs = {
    "medal-summary": useRef(null),
    "tournament-participation": useRef(null),
    "player-performance": useRef(null),
    "age-group-performance": useRef(null),
    "ranking-progression": useRef(null),
    "gender-split": useRef(null),
  };

  // --------------------------------------------------
  // 1️⃣ LOAD ALL DATA FOR THIS COACH
  // --------------------------------------------------
  useEffect(() => {
    async function fetchAll() {
      try {
        const [pRes, rRes, ajssRes, actRes] = await Promise.all([
          fetch(`${API_BASE}/api/performance-players`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()),

          fetch(`${API_BASE}/api/performance-results`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()),

          fetch(`${API_BASE}/api/performance-ajss`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()),

          fetch(`${API_BASE}/api/performance-activities`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.json()),
        ]);

        if (!pRes.success || !rRes.success || !ajssRes.success || !actRes.success) {
          showToast("Failed to load team report data", "error");
          return;
        }

        // Players under this coach
        setPlayers(pRes.players);

        // All results in system
        setResults(rRes.results);

        // All AJSS
        setAjss(ajssRes.ajss);

        // All tournament activities
        setActivities(actRes.activities);

        // Build year list (from results + ajss)
        const uniqueYears = [
          ...new Set([
            ...rRes.results.map(r => r.year),
            ...ajssRes.ajss.map(a => a.year),
          ]),
        ]
          .filter(Boolean)
          .sort((a, b) => b - a);

        setYears(uniqueYears);
        if (!selectedYear && uniqueYears.length > 0) {
          setSelectedYear(uniqueYears[0]);
        }
      } catch (err) {
        console.error(err);
        showToast("Error loading data", "error");
      }
    }

    fetchAll();
  }, []);

  // --------------------------------------------------
  // 2️⃣ FILTER RESULTS ONLY FOR THIS TEAM
  // --------------------------------------------------
  const playerIds = players.map(p => p.id);

  const teamResults = results.filter(r => playerIds.includes(r.playerId));
  const teamAjss = ajss.filter(a => playerIds.includes(a.playerId));

  const filteredResults = selectedYear
    ? teamResults.filter(r => r.year === selectedYear)
    : teamResults;

  const filteredAjss = selectedYear
    ? teamAjss.filter(a => a.year === selectedYear)
    : teamAjss;

  const filteredActivities = selectedYear
    ? activities.filter(act => {
        const start = act.startDate?.toDate ? act.startDate.toDate() : new Date(act.startDate);
        return start.getFullYear() === selectedYear;
      })
    : activities;

  // --------------------------------------------------
  // 3️⃣ SECTION NAVIGATION
  // --------------------------------------------------
  const sections = [
    { id: "medal-summary", title: "🥇 Medal Summary" },
    { id: "tournament-participation", title: "🏆 Tournament Participation" },
    { id: "player-performance", title: "👤 Player Performance" },
    { id: "age-group-performance", title: "📊 Age Group Performance" },
    { id: "ranking-progression", title: "📈 Ranking Progression" },
    { id: "gender-split", title: "👦👧 Gender Split" },
  ];

  const handleNavClick = (id) => {
    expandRefs[id]?.current?.();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  // Scroll-to-top
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 120);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // --------------------------------------------------
  // 4️⃣ RENDER
  // --------------------------------------------------
  return (
    <div className="flex w-full flex-grow">
      {/* Desktop Sidebar */}
      <aside 
        className="
          hidden lg:block
          w-64
          shrink-0
          sticky
          top-[96px]
          self-start
        "
      >
        <nav className="bg-white border rounded-lg shadow p-4 space-y-2">
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => handleNavClick(s.id)}
              className="block w-full text-left px-3 py-2 rounded hover:bg-blue-100"
            >
              {s.title}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-grow w-full max-w-5xl px-2 mx-auto space-y-6">
        {/* Header */}
        <div className="mb-6 relative flex items-center pt-8">
          <button
            onClick={() => navigate("/admin-performance-review-landing")}
            className="absolute left-0 flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <FaArrowLeft />
            <span className="hidden sm:inline">Back</span>
          </button>

          <h1 className="absolute left-1/2 transform -translate-x-1/2 text-3xl sm:text-2xl">
            Team Report
          </h1>

          {/* MOBILE MENU BUTTON */}
          <div
            className={`
              lg:hidden
              ${showScrollTop
                ? "fixed top-[80px] right-2 z-30"
                : "ml-auto relative"}
            `}
          >
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`
                px-3 py-2 text-2xl rounded
                ${showScrollTop ? "bg-blue-600 text-white shadow-lg" : "text-blue-600"}
              `}
            >
              <FaBars />
            </button>

            {/* DROPDOWN MENU */}
            {menuOpen && (
              <div
                className={`
                  absolute
                  ${showScrollTop ? "top-10 right-0" : "mt-2 right-0"}
                  bg-white border rounded shadow-lg w-64 z-50
                `}
              >
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleNavClick(s.id)}
                    className="block w-full text-left px-4 py-2 hover:bg-blue-100"
                  >
                    {s.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Year Selector */}
        <div className="flex justify-end my-4">
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-400 rounded px-3 py-2"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Sections */}
        <Section id="medal-summary" title="🥇 Medal Summary" expandRef={expandRefs["medal-summary"]}>
          <MedalSummary results={filteredResults} />
        </Section>

        <Section id="tournament-participation" title="🏆 Tournament Participation" expandRef={expandRefs["tournament-participation"]}>
          <TournamentParticipation
            results={filteredResults}
            activities={filteredActivities}
          />
        </Section>

        <Section id="player-performance" title="👤 Player Performance" expandRef={expandRefs["player-performance"]}>
          <PlayerPerformance results={filteredResults} players={players} />
        </Section>

        <Section id="age-group-performance" title="📊 Age Group Performance" expandRef={expandRefs["age-group-performance"]}>
          <AgeGroupPerformance results={filteredResults} ajssRankings={filteredAjss} />
        </Section>

        <Section id="ranking-progression" title="📈 Ranking Progression" expandRef={expandRefs["ranking-progression"]}>
          <RankingProgression ajssRankings={filteredAjss} selectedYear={selectedYear} />
        </Section>

        <Section id="gender-split" title="👦👧 Gender Split" expandRef={expandRefs["gender-split"]}>
          <GenderSplit players={players} results={filteredResults} />
        </Section>
      </main>

      {/* Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 right-6 p-3 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 z-50"
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
}
